import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button } from '@material-ui/core';
import { makeStyle } from '../style';
import { Header } from '../../../components/Header';
import { PAGES, STATUS } from '../../../utils/constant';
import HCServices from '../../../services/hcServices';
import Icon from '../../../utils/icons';
import LinkingForm from './LinkingForm';
import AirHvacDiagramView from './AirHvacDiagramView';
import ToastMessage from '../../../components/ToastMessage';
import I18n from '../../../localization';
import { PopUp } from '../../../components';

const pages = { diagram: 'diagram', linkingForm: 'linkingForm', successPage: 'successPage' };

const LinkHvacToThermostat = ({ iqAirModel, siteId, handleClick,fromManageHVACSystem, ...props }) => {
  const classes = makeStyle();
  const IQ_AIR_MODEL={IQ_AIR_HUB: {HEADER_TITLE:I18n.t('mobile.AirDeviceProvisioning.addIQAirHubTitle')},IQ_AIR_DISPLAY: {HEADER_TITLE:I18n.t('mobile.AirDeviceProvisioning.addIQAirDisplayTitle'), ID: 'IQ_AIR_DISPLAY',}}

  const [airDevices, setAirDevices] = useState([]);
  const [hvacs, setHvacs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showPage, setShowPage] = useState(pages.diagram);
  const [isLinkingDone, setIsLinkingDone] = useState(false);
  const [toast, setToast] = useState({ status: false, message: null, type: 'success' });
  const [hvacToDelete, setHvacToDelete] = useState(null);
  const [showPopup, setShowPopup] = useState(false);

  const showError = useCallback(() => {
    setToast({ status: true, message: I18n.t('mobile.common.errorMsg'), type: 'error' });
  }, []);

  const canAddHvac = useMemo(() => hvacs.length < airDevices.length, [hvacs.length, airDevices.length]);
  const hasSecondary = useMemo(() => airDevices.some(d => !d.primary), [airDevices]);
  const isLinkingPending = useMemo(() => airDevices.some(d => !d.primary && !d['linked-hvac']), [airDevices]);
  const isLinkingPendingForNewlyHvacAdded = useMemo(() => hvacs.some(d => d["linked-air-devices"].length == 0), [hvacs]);

  const headerTitle = useMemo(
    () => {
     if(fromManageHVACSystem)return I18n.t('mobile.AirDeviceProvisioning.hvacSystem') 
     if(showPage === pages.linkingForm) return I18n.t('mobile.AirDeviceProvisioning.linkHvacTitle')
     return IQ_AIR_MODEL[iqAirModel].HEADER_TITLE
    },
    [showPage, iqAirModel,fromManageHVACSystem]
  );

  const handleBack = useCallback(() => {
    
    if (showPage === pages.linkingForm || showPage === pages.successPage) {
       return setShowPage(pages.diagram);
      }
    if(iqAirModel === IQ_AIR_MODEL.IQ_AIR_DISPLAY.ID){
        return  handleClick(PAGES.AIR_DEVICE_INTEGRATION,PAGES.LINK_HVAC_TO_THERMOSTAT);
      }
      
     handleClick(PAGES.AIR_DEVICES_PROVISION,PAGES.LINK_HVAC_TO_THERMOSTAT);
  }, [showPage, handleClick]);

  const fetchDevices = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await HCServices.getConnectedDevices(siteId);
      if (res.status === STATUS.FAILURE) return showError();
      setAirDevices(res.data['air-devices'] ?? []);
      setHvacs(res.data['hvac-devices'] ?? []);
    } catch(e){
      console.info('error:',e)
      showError();
    }finally {
      setIsLoading(false);
    }
  }, [siteId, showError]);

  const addNewHvac = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await HCServices.addNewHvac(siteId);
      if (res.status === STATUS.FAILURE) showError();
      await fetchDevices();
    } catch(e){
      console.info('error:',e)
      showError();
    } finally {
      setIsLoading(false);
    }
  }, [siteId, fetchDevices, showError]);

  const deleteHvac = useCallback(
    async hvacId => {
      if (!hvacId) return;

      setIsLoading(true);
      try {
        const res = await HCServices.deleteHvac(siteId, hvacId);
        if (res.status === STATUS.FAILURE) showError();
        await fetchDevices();
      } catch(e){
        console.info('error:',e)
        showError();
      }finally {
        setIsLoading(false);
      }
    },
    [siteId, fetchDevices, showError]
  );


  const rescan = useCallback(async () => {
    try {
      setIsLoading(true);
      await HCServices.getAirDevices(siteId, true);
      await fetchDevices();
    } catch (e) {
      console.log('error on rescan', e);
    } finally {
      setIsLoading(false);
    }
  }, [siteId, fetchDevices]);

  const handleDeleteHvac = useCallback((id) => {
  setHvacToDelete(id);
  setShowPopup(true);
  }, []);

  const handleUpdatedView = useCallback(async devices => {
    if (!devices.length) return;
    setIsLoading(true);
    setShowPage(pages.diagram);

    const payload = {
      devices: devices.map(d => ({
        'serial-number': d['serial-number'],
        'hvac-id': d['linked-hvac'] ?? null,
      })),
    };

    try {
      const res = await HCServices.linkHvac(payload, siteId);
      if (res.status === STATUS.FAILURE) showError();
      await fetchDevices();
      setIsLinkingDone(true);
    } catch(e){
      console.info('error:',e)
      showError();
    } finally {
      setIsLoading(false);
    }
  }, [siteId, fetchDevices, showError]);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  useEffect(() => {
    if (showPage === pages.successPage) {
      handleClick(PAGES.AIR_DEVICE_INTEGRATION, PAGES.LINK_HVAC_TO_THERMOSTAT, 'DEVICE_LINKED_SUCCESSFULLY');
    }
  }, [showPage, handleClick]);

  const hvacName = hvacs.find(h => h['device-id'] === hvacToDelete)?.name;

  return (
    <>
      {!props.isWeb && <Header title={headerTitle} onClick={handleBack} />}

      {toast.status && (
        <Box className={classes.toastPosition}>
          <ToastMessage shouldDisplay content={toast.message}
            handleClose={() => setToast({ status: false })}
            type={toast.type} isWeb={props.isWeb} />
        </Box>
      )}

      <Box className={classes.modelcontainer}>
        {isLoading ? (
          <Box className={classes.loaderBtn}><Icon src="loaderV4" width="40px" height="40px" /></Box>
        ) : (
          <div className={classes.customContainer}>
            {showPage === pages.diagram ? (
              <AirHvacDiagramView
                isWeb={props.isWeb}
                classes={classes}
                airDevices={airDevices}
                hvacs={hvacs}
                canAddHvac={canAddHvac}
                hasSecondary={hasSecondary}
                fromManageHVACSystem={fromManageHVACSystem}
                isLinkingPending={isLinkingPending}
                isLinkingPendingForNewlyHvacAdded={isLinkingPendingForNewlyHvacAdded}
                onAddHvac={addNewHvac}
                onDeleteHvac={handleDeleteHvac}
                onLinkClick={setShowPage}
                isLinkingDone={isLinkingDone}
                handleBack={handleBack}
                rescan={rescan}
                {...props}
              />
            ) : (
              <LinkingForm airDevices={airDevices} hvacs={hvacs} handleUpdatedView={handleUpdatedView} />
            )}
          </div>
        )}
      </Box>

      <PopUp
        open={showPopup}
        handleClose={() => setShowPopup(false)}
        zIndex={10001}
        content={
          <div className={classes.popUpBox}>
            <div className={classes.popUpContent}>
              <div className={classes.iconBox}><Icon src="warningOrange" fill="#6B7073" /></div>
              <div className={classes.popUpMessage}>
                <span>{I18n.t('mobile.linkHvacToThermostat.wantToRemove', { hvacName: hvacName })}</span>
              </div>
              <div className={classes.linkButtonContainer} style={{ flexDirection: 'row' }}>
                <Button className={classes.buttonSerialStyle} style={{ width: 126 }} onClick={() => setShowPopup(false)}>{I18n.t('mobile.linkHvacToThermostat.no')}</Button>
                <Button className={classes.buttonStyle} style={{ width: 126 }}
                  onClick={() => {
                    setShowPopup(false);
                    deleteHvac(hvacToDelete);
                    setHvacToDelete(null);
                  }}
                >
                {I18n.t('mobile.linkHvacToThermostat.yes')}
              </Button>
              </div>
            </div>
          </div>
        }
      />
    </>
  );
};

export default LinkHvacToThermostat;
